import {
  Component,
  Element,
  Event,
  EventEmitter,
  Fragment,
  JSX,
  Method,
  Prop,
  State,
  Watch,
  h,
} from '@stencil/core'
import IMask from 'imask'

import { AnyMaskedOptions, InputType } from './air-input.types'
import { InputPresets } from './air-input.presets'
import { KeyCode } from '../../enums/keycode.enum'
import { Validation } from '../../types/status.types'
import { isUndefined } from '../../utils/helpers'

function getPresetMaskOptions(
  type: InputType,
  dynamicOptions?: Partial<IMask.AnyMaskedOptions>,
): IMask.AnyMaskedOptions | undefined {
  const inputPreset: IMask.AnyMaskedOptions | undefined = InputPresets[type]
  if (!inputPreset) {
    return
  }
  return Object.assign({}, inputPreset, dynamicOptions)
}

function isNumber(value: string | number | undefined): boolean {
  return typeof value === 'number' || !isNaN(Number(value))
}

@Component({
  tag: 'air-input',
  styleUrl: 'air-input.scss',
  shadow: true,
})
export class AirInput {
  /** Reference to the host element */
  @Element() host!: HTMLAirInputElement

  /** Defines name */
  @Prop({ reflect: true }) name?: string

  /** Specifies input label */
  @Prop() label?: string

  /** Specifies input type */
  @Prop() type: InputType = 'text'

  /** Defines input value */
  @Prop({ mutable: true }) value?: string
  @Watch('value')
  watchValueChanged(newValue: string, oldValue: string): void {
    if (newValue !== oldValue) {
      this.setInputValue(newValue)
      this.inputMask?.updateControl()
    }
  }

  /** Specifies placeholder value */
  @Prop() placeholder?: string

  /** Specifies if input should have autofocus */
  // eslint-disable-next-line @stencil-community/reserved-member-names
  @Prop() autofocus = false

  /** Specifies if input is read only */
  @Prop() readonly = false

  /** Specifies if input is required */
  @Prop() required = false

  /** Specifies if input is disabled */
  @Prop() disabled = false

  /** Defines maximum characters number */
  @Prop() maxlength?: number

  /** Defines minimum number value */
  @Prop() min?: number

  /** Defines minimum number value */
  @Prop() max?: number

  /** Specifies mask options for input */
  @Prop() maskOptions?: AnyMaskedOptions // TODO: Should be replaced by IMask type once available

  /** Specifies type of emitted value */
  @Prop() maskValue?: 'value' | 'unmaskedValue' | 'typedValue' // TODO: Should be replaced by IMask type once available

  /** Defines decimals number after point */
  @Prop() decimals = 0

  /** Specifies validation of input */
  @Prop() validation?: Validation

  /** Defines icon on the left side */
  @Prop() icon?: string

  /** Defines icon focusable on the left side */
  @Prop() iconFocusable = false

  /** Defines the icon clear text */
  @Prop() iconClear = 'x'

  /** Defines the icon for button shows password */
  @Prop() showPasswordIcon = 'eye'

  /** Defines the icon for button hides password */
  @Prop() hidePasswordIcon = 'eye-off'

  /** Triggers when user input value */
  @Event({ eventName: 'air-input' }) input!: EventEmitter<string>

  /** Triggers when input value was changed and blur */
  @Event({ eventName: 'air-change' }) change!: EventEmitter<string>

  /** Triggers when input in focus */
  @Event({ eventName: 'air-focus' }) focus!: EventEmitter<string>

  /** Triggers when an input has lost focus */
  @Event({ eventName: 'air-blur' }) blur!: EventEmitter<string>

  /** Triggers event on an input icon click */
  @Event({ eventName: 'air-icon-click' }) iconClick?: EventEmitter<MouseEvent>

  /** Triggers event on an input icon keydown */
  @Event({ eventName: 'air-icon-keydown' }) iconKeyDown?: EventEmitter<KeyboardEvent>

  private inputElement?: HTMLInputElement
  private inputMask?: IMask.InputMask<IMask.AnyMaskedOptions>
  private resetButton?: HTMLAirIconElement

  @State() isPasswordShown = false
  @State() hasValue = false

  /** Manually sets focus to the input element */
  @Method()
  async setFocus(): Promise<void> {
    this.inputElement && this.inputElement.focus && this.inputElement.focus()
  }

  /** Manually blur input element */
  @Method()
  async setBlur(): Promise<void> {
    this.inputElement?.blur()
  }

  componentWillLoad(): void {
    this.hasValue = this.value !== undefined && this.value !== ''
  }

  async componentDidLoad(): Promise<void> {
    this.setInputValue(this.value)
    this.handleToggleResetButtonVisibility()

    this.autofocus && (await this.setFocus())

    const maskOptions = this.getMaskOptions()

    if (!maskOptions) {
      return
    }

    if (this.inputElement) {
      this.inputMask = IMask(this.inputElement, maskOptions)
    }
  }

  disconnectedCallback(): void {
    this.inputMask?.destroy()
  }

  private getInputType(): string {
    if (['tel', 'email'].includes(this.type)) {
      return this.type
    }

    if (this.type === 'password' && !this.isPasswordShown) {
      return 'password'
    }

    return 'text'
  }

  private getMaskOptions(): IMask.AnyMaskedOptions | undefined {
    if (['number', 'currency'].includes(this.type)) {
      const dynamicOptions = {
        min: isNumber(this.min) ? Number(this.min) : undefined,
        max: isNumber(this.max) ? Number(this.max) : undefined,
        scale: isNumber(this.decimals) ? Number(this.decimals) : undefined,
      }
      return getPresetMaskOptions(this.type, dynamicOptions)
    }

    if (getPresetMaskOptions(this.type)) {
      return getPresetMaskOptions(this.type)
    }

    return this.maskOptions
  }

  private setInputValue(value = ''): void {
    if (this.inputElement) {
      this.inputElement.value = value
      this.inputMask?.updateValue()
    }
  }

  private resetValue = (): void => {
    this.value = ''
    this.setInputValue()
    this.inputElement?.focus()
    this.handleToggleResetButtonVisibility()
    this.input.emit(this.value)
  }

  private handleResetValue = (event: MouseEvent): void => {
    event.stopPropagation()
    this.resetValue()
    this.change.emit(this.value)
  }

  private togglePasswordVisibility = (): void => {
    this.isPasswordShown = !this.isPasswordShown
    this.inputElement?.focus()
  }

  private isValueEmpty(): boolean {
    return this.getEmittedValue() === '' || isUndefined(this.getEmittedValue())
  }

  private handleFocus = (): void => {
    if (this.disabled) return
    this.focus.emit(this.getEmittedValue())
  }

  private handleInput = (): void => {
    this.hasValue = !this.isValueEmpty()
    setTimeout(() => {
      this.handleToggleResetButtonVisibility()
      this.input.emit(this.getEmittedValue())
    })
  }

  private handleToggleResetButtonVisibility(): void {
    this.resetButton?.classList.toggle('hidden', this.isValueEmpty())
  }

  private handleChange = (): void => {
    if (this.disabled) return
    this.value = this.inputElement?.value
    this.change.emit(this.getEmittedValue())
  }

  private handleBlur = (): void => {
    this.blur.emit(this.getEmittedValue())
  }

  private getEmittedValue(): string | undefined {
    if (this.maskValue && this.inputMask) {
      return this.inputMask[this.maskValue].toString()
    }

    return this.inputElement?.value
  }

  private isTriggerKeyCode(event: KeyboardEvent): boolean {
    const keyCodes: string[] = [KeyCode.Enter, KeyCode.Space]
    return keyCodes.includes(event.code)
  }

  private handleResetValueKeyDown = (event: KeyboardEvent): void => {
    if (!this.isTriggerKeyCode(event)) return
    this.resetValue()
  }

  private handleTogglePasswordVisibilityKeyDown = (event: KeyboardEvent): void => {
    if (!this.isTriggerKeyCode(event)) return
    this.togglePasswordVisibility()
  }

  private renderLabel(): HTMLLabelElement | null {
    return this.label !== undefined ? (
      <label class="input-label" htmlFor="input">
        {this.label}
        {this.required && <span class="required-mark">*</span>}
      </label>
    ) : null
  }

  private renderValidation(): JSX.Element | null {
    return this.validation?.text !== undefined ? (
      <span class="status-text">{this.validation.text}</span>
    ) : null
  }

  private renderResetValueButton(): JSX.Element | null {
    if (this.disabled || this.readonly || this.type === 'password') {
      return null
    }
    const classes = {
      'input-icon-right': true,
      hidden: this.isValueEmpty(),
    }

    return (
      <air-icon
        ref={(el?: HTMLAirIconElement): void => {
          this.resetButton = el
        }}
        name={this.iconClear}
        class={classes}
        tabindex="0"
        aria-label="Reset text"
        onKeyDown={this.handleResetValueKeyDown}
        onClick={this.handleResetValue}
      ></air-icon>
    )
  }

  private renderPasswordVisibilityButton(): JSX.Element | null {
    if (this.type !== 'password') {
      return null
    }
    const eyeIcon = this.isPasswordShown ? this.hidePasswordIcon : this.showPasswordIcon
    const ariaLabel = this.isPasswordShown ? 'Show' : 'Hide'
    return (
      <air-icon
        class="input-icon-right"
        aria-label={ariaLabel}
        tabindex="0"
        name={eyeIcon}
        onKeyDown={this.handleTogglePasswordVisibilityKeyDown}
        onClick={this.togglePasswordVisibility}
      ></air-icon>
    )
  }

  private renderIcon(): JSX.Element | null {
    return this.icon !== undefined ? (
      <air-icon
        class="input-icon-left"
        name={this.icon}
        tabindex={this.iconFocusable ? 0 : -1}
        onClick={this.handleIconClick}
        onKeyDown={this.handleIconKeyDown}
      ></air-icon>
    ) : null
  }

  private handleIconClick = (event: MouseEvent): void => {
    event.stopPropagation()
    this.iconClick?.emit(event)
  }

  private handleIconKeyDown = (event: KeyboardEvent): void => {
    event.stopPropagation()
    this.iconKeyDown?.emit(event)
  }

  render(): JSX.Element {
    const inputAttributes = {
      id: 'input',
      type: this.getInputType(),
      class: 'input-element',
      disabled: this.disabled,
      placeholder: this.placeholder,
      readonly: this.readonly,
      maxlength: this.maxlength,
      ref: (el: HTMLInputElement | undefined): void => {
        this.inputElement = el
      },
      onFocus: this.handleFocus,
      onInput: this.handleInput,
      onChange: this.handleChange,
      onBlur: this.handleBlur,
    }

    const inputWrapperClasses = {
      'input-wrapper': true,
      [`with-status`]: this.validation?.status !== undefined,
      [`${this.validation?.status}`]: this.validation?.status !== undefined,
    }

    const inputElementWrapperClasses = {
      'input-element-wrapper': true,
      'with-icon': this.icon !== undefined,
      'has-value': this.hasValue,
    }

    return (
      <div class={inputWrapperClasses}>
        {this.renderLabel()}
        <div class={inputElementWrapperClasses}>
          {this.renderIcon()}
          <input {...inputAttributes} />
          {!this.disabled ? (
            <Fragment>
              {this.renderResetValueButton()}
              {this.renderPasswordVisibilityButton()}
            </Fragment>
          ) : null}
        </div>
        {this.renderValidation()}
      </div>
    )
  }
}
